import { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CartContext = createContext(null);

export const CartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("merari_cart") || "[]"); } catch { return []; }
  });
  const [products, setProducts] = useState([]);
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/products`).then((r) => setProducts(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    localStorage.setItem("merari_cart", JSON.stringify(items));
  }, [items]);

  const productsBySlug = useMemo(() => Object.fromEntries(products.map((p) => [p.slug, p])), [products]);

  const add = useCallback((slug, qty = 1) => {
    setItems((prev) => {
      const found = prev.find((i) => i.slug === slug);
      if (found) return prev.map((i) => (i.slug === slug ? { ...i, qty: i.qty + qty } : i));
      return [...prev, { slug, qty }];
    });
  }, []);

  const setQty = useCallback((slug, qty) => {
    setItems((prev) => (qty <= 0 ? prev.filter((i) => i.slug !== slug) : prev.map((i) => (i.slug === slug ? { ...i, qty } : i))));
  }, []);

  const remove = useCallback((slug) => setItems((prev) => prev.filter((i) => i.slug !== slug)), []);
  const clear = useCallback(() => setItems([]), []);
  const openCart = useCallback(() => setIsOpen(true), []);
  const closeCart = useCallback(() => setIsOpen(false), []);

  const count = items.reduce((s, i) => s + i.qty, 0);
  const subtotal = items.reduce((s, i) => s + (productsBySlug[i.slug]?.price || 0) * i.qty, 0);

  const checkout = useCallback(async () => {
    const { data } = await axios.post(`${API}/payments/cart-checkout`, {
      items: items.map((i) => ({ slug: i.slug, quantity: i.qty })),
      origin_url: window.location.origin,
    });
    window.location.href = data.checkout_url;
  }, [items]);

  return (
    <CartContext.Provider value={{ items, products, productsBySlug, add, setQty, remove, clear, count, subtotal, isOpen, openCart, closeCart, checkout }}>
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => useContext(CartContext);
